import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import StudioSidebar from '../../components/ui/StudioSidebar';

const StudioAPIKeys = () => {
  const { user, session, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState([]);
  const [domainWhitelist, setDomainWhitelist] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [showKeyRevealModal, setShowKeyRevealModal] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [expandedKeyId, setExpandedKeyId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Only studio_admin role is allowed
    if (!user || userProfile?.role !== 'studio_admin') {
      navigate('/');
      return;
    }

    if (session?.access_token) {
      loadData();
    }
  }, [user, session, userProfile, navigate]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    // Wait for session to be available
    if (!session?.access_token) {
      console.log('Session not ready, waiting...');
      setLoading(false);
      return;
    }

    try {
      // Load API keys
      const keysResponse = await fetch('/api/studio/api-keys', {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (keysResponse.ok) {
        const keysData = await keysResponse.json();
        setApiKeys(keysData.apiKeys || []);
      }

      // Load domain whitelist
      const domainsResponse = await fetch('/api/studio/domains', {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (domainsResponse.ok) {
        const domainsData = await domainsResponse.json();
        setDomainWhitelist(domainsData.domains || []);
      }

    } catch (err) {
      setError('Failed to load API keys and domains');
      console.error('API Keys load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const createAPIKey = async (keyData) => {
    try {
      const response = await fetch('/api/studio/api-keys', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(keyData)
      });

      if (response.ok) {
        const result = await response.json();
        setShowCreateModal(false);
        // Show the plaintext key in a modal (only time it's visible!)
        setNewlyCreatedKey(result);
        setShowKeyRevealModal(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create API key');
      }
    } catch (err) {
      setError('Failed to create API key');
      console.error('Create API key error:', err);
    }
  };

  const revokeAPIKey = async (keyId) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/studio/api-keys?id=${keyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to revoke API key');
      }
    } catch (err) {
      setError('Failed to revoke API key');
      console.error('Revoke API key error:', err);
    }
  };

  const addDomain = async (domain, apiKeyId = null) => {
    try {
      const response = await fetch('/api/studio/domains', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ domain, apiKeyId })
      });

      if (response.ok) {
        await loadData();
        setShowDomainModal(false);
        setSelectedKey(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to add domain');
      }
    } catch (err) {
      setError('Failed to add domain');
      console.error('Add domain error:', err);
    }
  };

  const getDomainsForKey = (keyId) => {
    // Return domains specific to this key OR studio-wide (NULL api_key_id)
    return domainWhitelist.filter(d =>
      d.api_key_id === keyId || d.api_key_id === null
    );
  };

  const removeDomain = async (domainId) => {
    if (!confirm('Are you sure you want to remove this domain from the whitelist?')) {
      return;
    }

    try {
      const response = await fetch(`/api/studio/domains?id=${domainId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to remove domain');
      }
    } catch (err) {
      setError('Failed to remove domain');
      console.error('Remove domain error:', err);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Could add a toast notification here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const CreateAPIKeyModal = ({ onClose, onCreate }) => {
    const [formData, setFormData] = useState({
      keyName: '',
      permissions: {
        read: true,
        write: false,
        analytics: true
      },
      expiresAt: ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onCreate(formData);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Create API Key</h3>
            <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Key Name"
              value={formData.keyName}
              onChange={(e) => setFormData({...formData, keyName: e.target.value})}
              placeholder="e.g., Production Key"
              required
            />

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Permissions</label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.permissions.read}
                    onChange={(e) => setFormData({
                      ...formData,
                      permissions: {...formData.permissions, read: e.target.checked}
                    })}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Read Access</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.permissions.write}
                    onChange={(e) => setFormData({
                      ...formData,
                      permissions: {...formData.permissions, write: e.target.checked}
                    })}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Write Access</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.permissions.analytics}
                    onChange={(e) => setFormData({
                      ...formData,
                      permissions: {...formData.permissions, analytics: e.target.checked}
                    })}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Analytics Access</span>
                </label>
              </div>
            </div>

            <Input
              label="Expires At (Optional)"
              type="datetime-local"
              value={formData.expiresAt}
              onChange={(e) => setFormData({...formData, expiresAt: e.target.value})}
            />

            <div className="flex justify-end space-x-3">
              <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
              <Button type="submit">Create Key</Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const AddDomainModal = ({ onClose, onAdd, apiKey }) => {
    const [domain, setDomain] = useState('');

    const handleSubmit = (e) => {
      e.preventDefault();
      onAdd(domain, apiKey?.id);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {apiKey ? `Add Domain to ${apiKey.key_name}` : 'Add Studio-Wide Domain'}
            </h3>
            <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="e.g., yourstudio.com"
              required
            />

            <div className="text-sm text-muted-foreground">
              <p>Add domains where your games can be played. Common examples:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>localhost (for local development)</li>
                <li>yourstudio.com (your website)</li>
                <li>kongregate.com (distribution platform)</li>
              </ul>
              {apiKey && (
                <p className="mt-3 text-xs bg-blue-50 dark:bg-blue-950/30 p-2 rounded border border-blue-200 dark:border-blue-800">
                  <strong>Key-Specific Domain:</strong> This domain will only work with the "{apiKey.key_name}" API key.
                  Perfect for distributing games to specific platforms.
                </p>
              )}
              {!apiKey && (
                <p className="mt-3 text-xs bg-yellow-50 dark:bg-yellow-950/30 p-2 rounded border border-yellow-200 dark:border-yellow-800">
                  <strong>Studio-Wide Domain:</strong> This domain will work with ALL your API keys.
                  Use for your own testing environments.
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
              <Button type="submit">Add Domain</Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading API keys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <StudioSidebar />
      
      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border lg:hidden">
          <div className="px-4 py-4">
            <h1 className="text-xl font-bold text-foreground">API Keys</h1>
          </div>
        </div>

        <main className="p-4 lg:p-8 pt-20 lg:pt-8 pb-20 lg:pb-8">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={20} className="text-red-600 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setError(null)}
                  className="ml-auto text-red-600 hover:text-red-800"
                />
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                API Keys & Domain Security
              </h1>
              <p className="text-muted-foreground">
                Manage your API keys and their whitelisted domains for secure game distribution
              </p>
            </div>

            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                size="sm"
                iconName="Globe"
                onClick={() => {
                  setSelectedKey(null);
                  setShowDomainModal(true);
                }}
              >
                Add Studio-Wide Domain
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Key"
                onClick={() => setShowCreateModal(true)}
              >
                Create API Key
              </Button>
            </div>
          </div>

          {/* API Keys Section */}
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-foreground">API Keys</h3>
              <div className="text-sm text-muted-foreground">
                {apiKeys.length} key{apiKeys.length !== 1 ? 's' : ''}
              </div>
            </div>

            {apiKeys.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Key" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h4 className="text-lg font-medium text-foreground mb-2">No API Keys</h4>
                <p className="text-muted-foreground mb-4">
                  Create your first API key to start integrating the SDK
                </p>
                <Button onClick={() => setShowCreateModal(true)}>
                  Create API Key
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {apiKeys.map((key) => {
                  const keyDomains = getDomainsForKey(key.id);
                  const isExpanded = expandedKeyId === key.id;

                  return (
                    <div key={key.id} className="border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-medium text-foreground">{key.key_name}</h4>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              key.status === 'active' ? 'bg-green-100 text-green-800' :
                              key.status === 'revoked' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {key.status}
                            </span>
                          </div>

                          <div className="bg-muted rounded p-3 mb-3">
                            <div className="flex items-center justify-between">
                              <code className="text-sm font-mono text-foreground">
                                {key.api_key || `${key.key_prefix}••••••••••••••••••••`}
                              </code>
                              {key.api_key && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconName="Copy"
                                  onClick={() => copyToClipboard(key.api_key)}
                                >
                                  Copy
                                </Button>
                              )}
                              {!key.api_key && (
                                <span className="text-xs text-muted-foreground">
                                  (Hashed - not retrievable)
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                            <div>
                              <span className="text-muted-foreground">Created:</span>
                              <p className="text-foreground">
                                {new Date(key.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Last Used:</span>
                              <p className="text-foreground">
                                {key.last_used_at
                                  ? new Date(key.last_used_at).toLocaleDateString()
                                  : 'Never'
                                }
                              </p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Expires:</span>
                              <p className="text-foreground">
                                {key.expires_at
                                  ? new Date(key.expires_at).toLocaleDateString()
                                  : 'Never'
                                }
                              </p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Permissions:</span>
                              <div className="flex space-x-1 mt-1">
                                {key.permissions?.read && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800">R</span>
                                )}
                                {key.permissions?.write && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-800">W</span>
                                )}
                                {key.permissions?.analytics && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-800">A</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Domain Management Section */}
                          <div className="border-t border-border pt-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
                              onClick={() => setExpandedKeyId(isExpanded ? null : key.id)}
                              className="mb-2"
                            >
                              Whitelisted Domains ({keyDomains.length})
                            </Button>

                            {isExpanded && (
                              <div className="mt-2 space-y-2">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-xs text-muted-foreground">
                                    Domains where this key will work (in addition to solisgames.com)
                                  </p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    iconName="Plus"
                                    onClick={() => {
                                      setSelectedKey(key);
                                      setShowDomainModal(true);
                                    }}
                                  >
                                    Add Domain
                                  </Button>
                                </div>

                                {keyDomains.length === 0 ? (
                                  <div className="text-center py-4 bg-muted/30 rounded">
                                    <Icon name="Shield" size={32} className="text-muted-foreground mx-auto mb-2" />
                                    <p className="text-sm text-muted-foreground">
                                      No whitelisted domains - restricted to Solis Games only
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Add domains to allow external distribution
                                    </p>
                                  </div>
                                ) : (
                                  <div className="space-y-1">
                                    {keyDomains.map((domain) => (
                                      <div key={domain.id} className="flex items-center justify-between bg-muted/30 rounded px-3 py-2">
                                        <div className="flex items-center space-x-2">
                                          <Icon name="Globe" size={14} className="text-muted-foreground" />
                                          <span className="text-sm text-foreground">{domain.domain}</span>
                                          {domain.api_key_id === null && (
                                            <span className="text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-0.5 rounded">
                                              Studio-wide
                                            </span>
                                          )}
                                          {domain.api_key_id === key.id && (
                                            <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-0.5 rounded">
                                              Key-specific
                                            </span>
                                          )}
                                        </div>
                                        {domain.api_key_id === key.id && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            iconName="Trash2"
                                            onClick={() => removeDomain(domain.id)}
                                            className="text-destructive hover:text-destructive"
                                          />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex space-x-2 ml-4">
                          {key.status === 'active' && (
                            <Button
                              variant="destructive"
                              size="sm"
                              iconName="Trash2"
                              onClick={() => revokeAPIKey(key.id)}
                            >
                              Revoke
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Info Section about Domain Management */}
          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={24} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">About Domain Whitelisting</h3>
                <div className="text-sm text-blue-800 dark:text-blue-200 space-y-2">
                  <p>
                    <strong>Key-Specific Domains:</strong> Add domains to individual API keys for secure, isolated distribution.
                    Perfect for distributing games to specific platforms (Kongregate, Itch.io, etc.)
                  </p>
                  <p>
                    <strong>Studio-Wide Domains:</strong> Add domains that work with ALL your API keys.
                    Useful for your own testing environments and development servers.
                  </p>
                  <p className="text-xs mt-2">
                    All keys work on solisgames.com and localhost by default. Keys without whitelisted domains
                    are restricted to Solis Games platform only.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateAPIKeyModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createAPIKey}
        />
      )}

      {showDomainModal && (
        <AddDomainModal
          onClose={() => {
            setShowDomainModal(false);
            setSelectedKey(null);
          }}
          onAdd={addDomain}
          apiKey={selectedKey}
        />
      )}

      {showKeyRevealModal && newlyCreatedKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-2xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">API Key Created Successfully!</h3>
                <p className="text-sm text-yellow-600 dark:text-yellow-500 font-medium">
                  ⚠️ IMPORTANT: Copy this key now - it will never be shown again!
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-950/30 border-2 border-yellow-500 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-foreground">Your API Key:</label>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Copy"
                  onClick={() => {
                    copyToClipboard(newlyCreatedKey.api_key);
                    alert('API key copied to clipboard!');
                  }}
                >
                  Copy to Clipboard
                </Button>
              </div>
              <code className="block bg-black/10 dark:bg-white/10 p-3 rounded text-sm font-mono text-foreground break-all">
                {newlyCreatedKey.api_key}
              </code>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Key Details:</h4>
              <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                <p><strong>Name:</strong> {newlyCreatedKey.key_name}</p>
                <p><strong>Type:</strong> {newlyCreatedKey.key_type}</p>
                <p><strong>Environment:</strong> {newlyCreatedKey.environment}</p>
                <p><strong>Rate Limit:</strong> {newlyCreatedKey.rate_limit_tier}</p>
              </div>
            </div>

            <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-red-900 dark:text-red-100 mb-2">Security Notice:</h4>
              <ul className="text-sm text-red-800 dark:text-red-200 space-y-1 list-disc list-inside">
                <li>This is the ONLY time you'll see the full API key</li>
                <li>Store it securely (e.g., in a password manager or environment variables)</li>
                <li>Never commit it to version control</li>
                <li>If lost, you'll need to create a new key</li>
              </ul>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="default"
                onClick={() => {
                  setShowKeyRevealModal(false);
                  setNewlyCreatedKey(null);
                  loadData(); // Reload to show the hashed version
                }}
              >
                I've Copied My Key
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudioAPIKeys;
